import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { profileApi } from '../../services/api'
import { EmployeeBasic, EmployeePosition } from '../../types'
import { formatCurrencyWithUnit } from '../../utils/formatNumber'
import { formatDateJST } from '../../utils/dateUtils'
import { useAuth } from '../../contexts/AuthContext'
import { canViewSalaryRate, getStatusLabel, EvaluationStatus } from '../../utils/statusLabels'

interface ProfileData {
  basic: EmployeeBasic
  currentPosition: EmployeePosition & {
    department_name: string
    department_type: string
    rank_cost: number
  }
  positionHistory: Array<EmployeePosition & {
    department_name: string
  }>
  evaluationHistory: Array<{
    evaluation_id: string
    evaluation_period: string
    performance_score: number
    salary_increase_rate: number
    status: string
    updated_at: string
  }>
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Email編集状態
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [editedEmail, setEditedEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)

  // パスワード変更ダイアログ
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordLoading, setPasswordLoading] = useState(false)

  // 履歴の展開状態
  const [positionHistoryExpanded, setPositionHistoryExpanded] = useState(false)
  const [evaluationHistoryExpanded, setEvaluationHistoryExpanded] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await profileApi.getProfile()

      if (response.data.success && response.data.data) {
        setProfile(response.data.data)
        setEditedEmail(response.data.data.basic.email)
      } else {
        setError('プロフィール情報の取得に失敗しました')
      }
    } catch (err: any) {
      console.error('プロフィール取得エラー:', err)
      setError(err.response?.data?.message || 'プロフィール情報の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleEmailEdit = () => {
    setIsEditingEmail(true)
    setEmailError(null)
  }

  const handleEmailCancel = () => {
    setIsEditingEmail(false)
    setEditedEmail(profile?.basic.email || '')
    setEmailError(null)
  }

  const handleEmailSave = async () => {
    try {
      setEmailError(null)

      // バリデーション
      if (!editedEmail) {
        setEmailError('メールアドレスを入力してください')
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(editedEmail)) {
        setEmailError('有効なメールアドレスを入力してください')
        return
      }

      const response = await profileApi.updateProfile(editedEmail)

      if (response.data.success) {
        setSuccess('メールアドレスを更新しました')
        setIsEditingEmail(false)
        loadProfile() // 最新情報を再取得

        // 成功メッセージを3秒後に消す
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err: any) {
      console.error('メールアドレス更新エラー:', err)
      setEmailError(err.response?.data?.message || 'メールアドレスの更新に失敗しました')
    }
  }

  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true)
    setPasswordError(null)
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
  }

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false)
    setPasswordError(null)
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
  }

  const handlePasswordChange = async () => {
    try {
      setPasswordError(null)
      setPasswordLoading(true)

      // バリデーション
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setPasswordError('全てのフィールドを入力してください')
        return
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('新しいパスワードと確認用パスワードが一致しません')
        return
      }

      if (passwordData.newPassword.length < 8) {
        setPasswordError('パスワードは8文字以上で設定してください')
        return
      }

      const response = await profileApi.changePassword(passwordData)

      if (response.data.success) {
        setSuccess('パスワードを変更しました')
        handlePasswordDialogClose()

        // 成功メッセージを3秒後に消す
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err: any) {
      console.error('パスワード変更エラー:', err)
      setPasswordError(err.response?.data?.message || 'パスワードの変更に失敗しました')
    } finally {
      setPasswordLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return formatDateJST(dateString)
  }

  const getJobGradeLabel = (grade: string) => {
    const labels: { [key: string]: string } = {
      '部長': '部長クラス',
      '課長': '課長クラス',
      '班長': '班長クラス',
      '役職なし': '一般職',
    }
    return labels[grade] || grade
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          プロフィール
        </Typography>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={loadProfile} sx={{ mt: 2 }}>
          再読み込み
        </Button>
      </Box>
    )
  }

  if (!profile) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          プロフィール
        </Typography>
        <Alert severity="info">プロフィール情報が見つかりません</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        プロフィール
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* 基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            基本情報
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                社員番号
              </Typography>
              <Typography variant="body1">
                {profile.basic.employee_id}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                氏名
              </Typography>
              <Typography variant="body1">
                {profile.basic.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                メールアドレス
              </Typography>
              {isEditingEmail ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                    type="email"
                  />
                  <IconButton color="primary" onClick={handleEmailSave}>
                    <SaveIcon />
                  </IconButton>
                  <IconButton onClick={handleEmailCancel}>
                    <CancelIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">
                    {profile.basic.email}
                  </Typography>
                  <IconButton size="small" onClick={handleEmailEdit}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                入社日
              </Typography>
              <Typography variant="body1">
                {formatDate(profile.basic.hire_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                勤続年数
              </Typography>
              <Typography variant="body1">
                {Math.floor((new Date().getTime() - new Date(profile.basic.hire_date).getTime()) / (1000 * 60 * 60 * 24 * 365))}年
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 現在の所属・職位情報 */}
      {profile.currentPosition && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              現在の所属・職位
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  部署
                </Typography>
                <Typography variant="body1">
                  {profile.currentPosition.department_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  部門種別
                </Typography>
                <Typography variant="body1">
                  {profile.currentPosition.department_type === 'tech' ? '技術部門' :
                   profile.currentPosition.department_type === 'sales' ? '営業部門' : '管理部門'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  職級
                </Typography>
                <Typography variant="body1">
                  {getJobGradeLabel(profile.currentPosition.job_grade)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  役職
                </Typography>
                <Typography variant="body1">
                  {profile.currentPosition.job_title}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  ランク
                </Typography>
                <Typography variant="body1">
                  {profile.currentPosition.rank}等級
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  年額ランクコスト
                </Typography>
                <Typography variant="body1">
                  {formatCurrencyWithUnit(profile.currentPosition.rank_cost)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  配属日
                </Typography>
                <Typography variant="body1">
                  {formatDate(profile.currentPosition.assignment_date)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  発効日
                </Typography>
                <Typography variant="body1">
                  {formatDate(profile.currentPosition.effective_from)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* パスワード変更 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              セキュリティ
            </Typography>
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={handlePasswordDialogOpen}
            >
              パスワード変更
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 所属・職位履歴 */}
      {profile.positionHistory && profile.positionHistory.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setPositionHistoryExpanded(!positionHistoryExpanded)}
            >
              <Typography variant="h6">
                所属・職位履歴
              </Typography>
              <IconButton>
                {positionHistoryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={positionHistoryExpanded}>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>部署</TableCell>
                      <TableCell>職級</TableCell>
                      <TableCell>役職</TableCell>
                      <TableCell>ランク</TableCell>
                      <TableCell>発効日</TableCell>
                      <TableCell>終了日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {profile.positionHistory.map((position, index) => (
                      <TableRow key={index}>
                        <TableCell>{position.department_name}</TableCell>
                        <TableCell>{getJobGradeLabel(position.job_grade)}</TableCell>
                        <TableCell>{position.job_title}</TableCell>
                        <TableCell>{position.rank}等級</TableCell>
                        <TableCell>{formatDate(position.effective_from)}</TableCell>
                        <TableCell>{position.effective_to ? formatDate(position.effective_to) : '現在'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* 評価履歴 */}
      {profile.evaluationHistory && profile.evaluationHistory.length > 0 && (
        <Card>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setEvaluationHistoryExpanded(!evaluationHistoryExpanded)}
            >
              <Typography variant="h6">
                評価履歴
              </Typography>
              <IconButton>
                {evaluationHistoryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={evaluationHistoryExpanded}>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>評価期間</TableCell>
                      <TableCell align="right">パフォーマンス評価点</TableCell>
                      <TableCell align="right">昇給率 (%)</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell>更新日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {profile.evaluationHistory.map((evaluation) => (
                      <TableRow key={evaluation.evaluation_id}>
                        <TableCell>{evaluation.evaluation_period}</TableCell>
                        <TableCell align="right">
                          {evaluation.performance_score !== null && evaluation.performance_score !== undefined
                            ? Number(evaluation.performance_score).toFixed(2)
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {canViewSalaryRate(evaluation.status as EvaluationStatus, user?.role || 'employee', false) ? (
                            evaluation.salary_increase_rate !== null && evaluation.salary_increase_rate !== undefined
                              ? Number(evaluation.salary_increase_rate).toFixed(2)
                              : '-'
                          ) : (
                            <Tooltip title="昇給率は評価確定後に表示されます">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, color: 'text.disabled' }}>
                                <VisibilityOffIcon fontSize="small" />
                                <Typography variant="body2">非表示</Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{getStatusLabel(evaluation.status as EvaluationStatus).label}</TableCell>
                        <TableCell>{formatDate(evaluation.updated_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* パスワード変更ダイアログ */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>パスワード変更</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            margin="normal"
            label="現在のパスワード"
            type="password"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="新しいパスワード"
            type="password"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
            helperText="8文字以上で設定してください"
          />
          <TextField
            fullWidth
            margin="normal"
            label="新しいパスワード（確認）"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose}>
            キャンセル
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={passwordLoading}
          >
            {passwordLoading ? <CircularProgress size={24} /> : '変更'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProfilePage
